import React, { useCallback, useEffect, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonContent,
  IonIcon,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSpinner,
  IonText,
  IonToast,
} from "@ionic/react";
import { checkmarkCircleOutline, refreshOutline } from "ionicons/icons";
import moment from "moment";

import Header from "src/components/Header";
import RutinaVista, { GARNET, numeroRutina } from "src/components/rutina/RutinaVista";
import RutinaAPI, { MiRutina as MiRutinaT, mensajeError } from "src/services/rutina";

/**
 * Mi Rutina (operarios) - TD-11191.
 * Rutina vigente de las asignaciones del operario y, si está cubriendo un
 * reemplazo, la de la asignación que cubre. Muestra las tareas del día (las
 * profundas según su frecuencia) y la rutina completa; el operario la acepta.
 */
const MiRutina: React.FC = () => {
  const [rutinas, setRutinas] = useState<MiRutinaT[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verTodo, setVerTodo] = useState<Record<number, boolean>>({});
  const [aceptando, setAceptando] = useState<number | null>(null);
  const [toast, setToast] = useState<{ show: boolean; msg: string; color?: string }>({ show: false, msg: "" });

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const r = await RutinaAPI.miRutina();
      setRutinas(r.data.datos.rutinas || []);
    } catch (err) {
      setError(mensajeError(err, "No se pudo cargar tu rutina."));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const aceptar = async (r: MiRutinaT) => {
    setAceptando(r.rutina_id);
    try {
      await RutinaAPI.aceptar(r.rutina_id);
      setRutinas((prev) =>
        prev.map((x) =>
          x.rutina_id === r.rutina_id ? { ...x, aceptada_en: moment().format("YYYY-MM-DD HH:mm") } : x
        )
      );
      setToast({ show: true, msg: "Rutina aceptada", color: "success" });
    } catch (err) {
      setToast({ show: true, msg: mensajeError(err, "No se pudo aceptar la rutina"), color: "danger" });
    } finally {
      setAceptando(null);
    }
  };

  return (
    <IonPage>
      <Header titulo="Mi Rutina" botonesDerecha={[{ icono: refreshOutline, action: cargar }]} />
      <IonContent id="mi-rutina" className="ion-padding">
        <IonText color="medium">
          <small style={{ textTransform: "capitalize" }}>
            {moment().locale("es-mx").format("dddd DD/MM/YYYY")}
          </small>
        </IonText>

        {cargando ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <IonSpinner name="crescent" />
          </div>
        ) : error ? (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        ) : rutinas.length === 0 ? (
          <IonText color="medium">
            <p>No tenés una rutina asignada por el momento.</p>
          </IonText>
        ) : (
          rutinas.map((r) => (
            <IonCard key={r.rutina_id} style={{ margin: "10px 0" }}>
              <IonCardContent>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div>
                    <h2 style={{ margin: 0, fontWeight: 700, color: GARNET }}>{r.ps_nombre}</h2>
                    <IonText color="medium">
                      <small>{numeroRutina(r)}</small>
                    </IonText>
                  </div>
                  {r.aceptada_en ? (
                    <IonBadge color="success">Aceptada</IonBadge>
                  ) : (
                    <IonBadge color="warning">Pendiente</IonBadge>
                  )}
                </div>
                {r.horario_hoy && (
                  <p style={{ margin: "6px 0 0" }}>
                    <IonText color="medium">
                      <small>
                        {r.horario_hoy.length ? `Tu horario de hoy: ${r.horario_hoy.join(" / ")}` : "Hoy no tenés horario asignado"}
                      </small>
                    </IonText>
                  </p>
                )}
                {r.rol === "REEMPLAZO" && (
                  <p style={{ margin: "6px 0 0" }}>
                    <IonBadge color="tertiary">Reemplazo</IonBadge>{" "}
                    <IonText color="medium">Cubrís a {r.titular_nombre}</IonText>
                  </p>
                )}

                <IonSegment
                  style={{ marginTop: 10 }}
                  value={verTodo[r.rutina_id] ? "todo" : "hoy"}
                  onIonChange={(e) => setVerTodo((v) => ({ ...v, [r.rutina_id]: e.detail.value === "todo" }))}
                >
                  <IonSegmentButton value="hoy">
                    <IonLabel>Hoy</IonLabel>
                  </IonSegmentButton>
                  <IonSegmentButton value="todo">
                    <IonLabel>Rutina completa</IonLabel>
                  </IonSegmentButton>
                </IonSegment>
                <div style={{ marginTop: 10 }}>
                  <RutinaVista sectores={r.sectores} soloHoy={!verTodo[r.rutina_id]} />
                </div>

                {r.observaciones && (
                  <div style={{ marginTop: 10 }}>
                    <strong>Observaciones</strong>
                    <p style={{ whiteSpace: "pre-wrap", margin: "4px 0 0" }}>{r.observaciones}</p>
                  </div>
                )}

                {!r.aceptada_en && (
                  <IonButton
                    expand="block"
                    style={{ marginTop: 12 }}
                    disabled={aceptando === r.rutina_id}
                    onClick={() => aceptar(r)}
                  >
                    <IonIcon slot="start" icon={checkmarkCircleOutline} /> Aceptar rutina
                  </IonButton>
                )}
              </IonCardContent>
            </IonCard>
          ))
        )}
        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={2500}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default MiRutina;
